/**
 * Links: the flexible relationships between records, and out to GitHub and
 * deployments (design doc sections 18, 19, 62).
 *
 *   INC000184  RESOLVED_BY        CHG000057
 *   INC000184  ENGINEERING_ISSUE  GITHUB_ISSUE 492
 *   CHG000057  DEPLOYED_BY        GITHUB_PR 493
 *   CHG000057  DEPLOYED_BY        DEPLOYMENT 281ab3c
 *
 * The INC stays the authoritative operational record; a GitHub issue is the
 * engineering work item when one is needed. Linking them is how "what changed"
 * becomes a click instead of an archaeology project.
 *
 * Ops-record targets are stored by NUMBER, not UUID: the number is stable,
 * human, and what the operator typed. The API resolves it on write (a link to
 * a record that does not exist is refused) and joins it on read.
 */

import { type RecordKind, parseRecordNumber } from './records.js';

export const LINK_RELATIONSHIPS = [
  'CAUSED_BY', 'RESOLVED_BY', 'RELATED_TO', 'DUPLICATE_OF', 'ENGINEERING_ISSUE', 'PULL_REQUEST', 'DEPLOYED_BY', 'SPAWNED',
] as const;
export type LinkRelationship = (typeof LINK_RELATIONSHIPS)[number];

export const LINK_TARGET_KINDS = ['INC', 'CHG', 'RITM', 'GITHUB_ISSUE', 'GITHUB_PR', 'DEPLOYMENT', 'URL'] as const;
export type LinkTargetKind = (typeof LINK_TARGET_KINDS)[number];

export function isOpsRecordTarget(kind: LinkTargetKind): kind is RecordKind {
  return kind === 'INC' || kind === 'CHG' || kind === 'RITM';
}

/** Relationships that mean "this incident and that change belong together" for the freeze rule. */
export const INCIDENT_CHANGE_RELATIONSHIPS: readonly LinkRelationship[] = ['CAUSED_BY', 'RESOLVED_BY', 'RELATED_TO', 'SPAWNED'];

const GITHUB_REF = /^(?:[\w.-]+\/[\w.-]+#)?\d+$/;
const SHA = /^[0-9a-f]{7,40}$/i;

/**
 * Validate a target reference for its kind. Returns a normalized reference,
 * or an error sentence. The normalization is what makes the unique index on
 * (source, relationship, target) mean something: 'inc000184' and 'INC000184'
 * are the same link.
 */
export function normalizeLinkTarget(kind: LinkTargetKind, ref: string): { ok: true; ref: string } | { ok: false; error: string } {
  const r = ref.trim();
  if (r === '') return { ok: false, error: 'A link needs a target.' };
  if (isOpsRecordTarget(kind)) {
    const parsed = parseRecordNumber(r);
    if (!parsed) return { ok: false, error: `"${r}" is not a record number (expected something like ${kind}000012).` };
    if (parsed.kind !== kind) return { ok: false, error: `"${r}" is a ${parsed.kind} number, not a ${kind}.` };
    return { ok: true, ref: r.toUpperCase() };
  }
  switch (kind) {
    case 'GITHUB_ISSUE':
    case 'GITHUB_PR':
      return GITHUB_REF.test(r) ? { ok: true, ref: r } : { ok: false, error: `"${r}" is not a GitHub number (492) or owner/repo#492.` };
    case 'DEPLOYMENT':
      return SHA.test(r) ? { ok: true, ref: r.toLowerCase() } : { ok: false, error: `"${r}" is not a commit SHA (7 to 40 hex characters).` };
    case 'URL':
      return /^https?:\/\/\S+$/i.test(r) ? { ok: true, ref: r } : { ok: false, error: `"${r}" is not an http(s) URL.` };
  }
}
